import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ProjectStatusData } from './project-status';

@Injectable({
  providedIn: 'root'
})
export class ProjectStatusService {
  private readonly apiUrl = '/api/project-status'; // Update with your actual API endpoint

  constructor(private http: HttpClient) {}

  /**
   * Fetch project status data from API
   * @param date Optional date parameter for filtering
   * @returns Observable of ProjectStatusData
   */
  // getProjectStatusData(date?: Date): Observable<ProjectStatusData> {
  //   let params = new HttpParams();
  //   if (date) {
  //     params = params.set('date', date.toISOString());
  //   }

  //   return this.http.get<ProjectStatusData>(this.apiUrl, { params }).pipe(
  //     map(data => this.validateAndTransformData(data)),
  //     catchError(error => {
  //       console.error('Error fetching project status data:', error);
  //       return of(this.getDefaultData());
  //     })
  //   );
  // }

  /**
   * Update project status data
   * @param data ProjectStatusData to update
   * @returns Observable of the updated data
   */
  updateProjectStatusData(data: ProjectStatusData): Observable<ProjectStatusData> {
    return this.http.put<ProjectStatusData>(this.apiUrl, data).pipe(
      map(response => this.validateAndTransformData(response)),
      catchError(error => {
        console.error('Error updating project status data:', error);
        throw error;
      })
    );
  }

  /**
   * Validate and transform incoming data
   * @param data Raw data from API
   * @returns Validated and transformed ProjectStatusData
   */
  private validateAndTransformData(data: any): ProjectStatusData {
    return {
      projectCompletion: this.validateNumber(data.projectCompletion, 0, 100, 0),
      plannedHours: this.validateNumber(data.plannedHours, 0, Infinity, 0),
      filledHours: this.validateNumber(data.filledHours, -Infinity, Infinity, 0),
      unfilledHours: this.validateNumber(data.unfilledHours, -Infinity, Infinity, 0),
      offsetHours: this.validateNumber(data.offsetHours, -Infinity, Infinity, 0)
    };
  }

  /**
   * Validate number values with min/max constraints
   * @param value Value to validate
   * @param min Minimum allowed value
   * @param max Maximum allowed value
   * @param defaultValue Default value if validation fails
   * @returns Validated number
   */
  private validateNumber(value: any, min: number, max: number, defaultValue: number): number {
    const num = Number(value);
    if (isNaN(num) || num < min || num > max) {
      return defaultValue;
    }
    return num;
  }

  /**
   * Get default data structure
   * @returns Default ProjectStatusData
   */
  private getDefaultData(): ProjectStatusData {
    return {
      projectCompletion: 40,
      plannedHours: 80,
      filledHours: 39.76,
      unfilledHours: -12.59,
      offsetHours: 9.5
    };
  }

  /**
   * Calculate derived metrics from project data
   * @param data ProjectStatusData
   * @returns Object with calculated metrics
   */
  calculateMetrics(data: ProjectStatusData) {
    const completionRate = data.plannedHours > 0 ? (data.filledHours / data.plannedHours) * 100 : 0;
    const efficiency = data.filledHours > 0 ? ((data.filledHours - data.offsetHours) / data.filledHours) * 100 : 0;

    return {
      completionRate: Math.min(completionRate, 100),
      efficiency: Math.max(efficiency, 0),
      remainingHours: Math.max(data.plannedHours - data.filledHours, 0),
      overTime: Math.max(data.filledHours - data.plannedHours, 0)
    };
  }
}
